import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import * as Sentry from "@sentry/capacitor";
import { LocalDate } from "@js-joda/core";
import { Icd10Code } from "../messages/IcdCode";
import { ICD10CodeId } from "../messages/ObjectIds";
import { nullToUndefined } from "./TrainingCenter/TrainingCenterLanguageLookup";

export interface IcdAnswer {
  icd: Icd10Code | null;
  date: LocalDate | null;
}

export function getEmptyIcdAnswer() {
  return {
    icd: null,
    date: null,
  };
}

export function validateIcdAnswer(answer: any): IcdAnswer | null {
  try {
    const parsed = JSON.parse(answer);

    if (parsed !== null) {
      return {
        icd:
          nullToUndefined(parsed.icd) !== undefined
            ? {
                id: ICD10CodeId.wrap(parsed.icd.id),
                dxCode: parsed.icd.dxCode,
                formattedDxCode: parsed.icd.formattedDxCode,
                shortDesc: parsed.icd.shortDesc,
                longDesc: parsed.icd.longDesc,
              }
            : null,
        date: nullToUndefined(parsed.date) !== undefined ? LocalDate.parse(parsed.date) : null,
      };
    }
  } catch (e) {
    console.error("Failed to parse ICD Answer", e);
    Sentry.captureException(e);
  }

  return null;
}

export function validateIcdChartAnswer(answer: any): IcdAnswer[] {
  const answers: IcdAnswer[] = [];

  try {
    const parsed = JSON.parse(answer);

    if (!Array.isArray(parsed) && parsed !== null) {
      throw new Error(`Expected ICD Chart answer to be array or null. got "${parsed}" instead`);
    }

    if (Array.isArray(parsed)) {
      answers.push(
        ...(parsed
          .map((_answer: any) => validateIcdAnswer(JSON.stringify(_answer)))
          .filter((_answer) => _answer !== null) as IcdAnswer[])
      );
    }

    if (answers.length === 0) {
      answers.push(getEmptyIcdAnswer());
    }
  } catch (e) {
    console.error("Failed to parse ICD Chart", e);
    Sentry.captureException(e);
  }

  return answers;
}

export async function scrollToViewAfterKeyboardOpen({ currentTarget }: React.MouseEvent) {
  if (!Capacitor.isPluginAvailable("Keyboard")) {
    return;
  }

  const scrollToView = (element: Element) => {
    (element as any).scrollIntoViewIfNeeded({ behavior: "smooth", block: "end" });
    listener.remove();
  };

  const listener = await Keyboard.addListener("keyboardDidShow", () => scrollToView(currentTarget));
}
